import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  notFound,
  redirect,
  useRouter
} from '@tanstack/react-router';
import { useCallback, type ReactElement } from 'react';
import { rememberReturnPath, takeReturnPath } from '../auth/return-path';
import { AccountDeletedRoute } from '../routes/AccountDeletedRoute';
import { AuthCallbackRoute } from '../routes/AuthCallbackRoute';
import { CardRoute } from '../routes/CardRoute';
import { ConnectedAgentsRoute } from '../routes/ConnectedAgentsRoute';
import { ConsentRoute } from '../routes/ConsentRoute';
import { ThemeSettings } from '../components/ThemeSettings';
import { ConnectRoute } from '../routes/ConnectRoute';
import { HomeRoute } from '../routes/HomeRoute';
import { LoginRoute } from '../routes/LoginRoute';
import { PrintRoute } from '../routes/PrintRoute';
import { PublishedLinksRoute } from '../routes/PublishedLinksRoute';
import { PublishedRoute } from '../routes/PublishedRoute';
import { ReportRoute } from '../routes/ReportRoute';
import { SignInRoute } from '../routes/SignInRoute';
import { BrandSettings } from '../routes/settings/BrandSettings';
import { SourcesSettings } from '../routes/settings/SourcesSettings';
import { DangerZone } from '../routes/settings/DangerZone';
import { GeneralSettings } from '../routes/settings/GeneralSettings';
import { SettingsLayout } from '../routes/settings/SettingsLayout';
import { TokenSettings } from '../routes/settings/TokenSettings';
import { WorkspaceLayout } from '../shell/WorkspaceLayout';
import { WorkspaceSwitcher } from '../shell/WorkspaceSwitcher';
import { ButtonLink } from '../ui/Button';
import { StatusState } from '../ui/StatusState';
import { NotAMemberError, type SessionSnapshot, type SessionStore } from './session';
import { useSession } from './session-context';
import { isGoogleErrorCode, type GoogleErrorCode } from '../sources/connect';

export interface RouterContext {
  session: SessionStore;
  queryClient: QueryClient;
}

/**
 * Sign-out for the account menu. The store never clears caches or navigates,
 * so this does both: the next person in this browser must not see this
 * person's workspaces or reports, which live in the query cache.
 * Clears last: a mounted query that re-renders while the logout is in flight
 * would refetch the previous person's data into an already-empty cache.
 */
export function useSignOut(): () => void {
  const { store } = useSession();
  const queryClient = useQueryClient();
  const router = useRouter();
  return useCallback(() => {
    void store
      .signOut()
      .then(() => router.navigate({ to: '/login', replace: true }))
      // In `finally`: a rejected sign-out or navigation must not leave this person's data cached.
      .finally(() => queryClient.clear())
      .catch(() => {});
  }, [store, queryClient, router]);
}

/** Unknown URLs, non-member workspaces and unknown settings sections. */
function NotFoundPage(): ReactElement {
  const { snapshot } = useSession();
  const signedIn = snapshot.status === 'signed-in';
  // Built before the return so the guard does not read this div's classes as classes on StatusState.
  const action = signedIn ? (
    <div className="bg-ink rounded-control p-2">
      <WorkspaceSwitcher workspaceId={snapshot.workspaceId ?? ''} variant="full" />
    </div>
  ) : (
    <ButtonLink href="/login">Sign in</ButtonLink>
  );
  return (
    <StatusState
      kind="empty"
      page
      title="Page not found"
      message="There is nothing here, or it belongs to a workspace you cannot open."
      action={action}
    />
  );
}

/** Today's waiting text while Supabase loads on the sign-in surfaces. */
function SupabaseWait({ failed }: { failed: boolean }): ReactElement {
  return (
    <StatusState
      kind={failed ? 'error' : 'loading'}
      page
      title={failed ? 'Sign-in is temporarily unavailable, try again.' : 'Loading…'}
    />
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ notFoundComponent: NotFoundPage });

/** The session as it is now, once it has loaded: `ready()` resolves to nothing, so read `getSnapshot()`. */
async function currentSession(session: SessionStore): Promise<SessionSnapshot> {
  await session.ready();
  return session.getSnapshot();
}

/** Waits for the session; signed out, remembers `href` (when given) and goes to sign-in. */
async function requireSignedIn(session: SessionStore, href?: string): Promise<void> {
  const snapshot = await currentSession(session);
  if (snapshot.status === 'signed-in') return;
  if (href !== undefined) rememberReturnPath(href);
  throw redirect({ to: '/login' });
}

// --- public, shell-less routes ---------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { method?: 'password' } => ({
    ...(s.method === 'password' ? { method: 'password' as const } : {})
  }),
  beforeLoad: async ({ context }) => {
    await context.session.ready();
  },
  component: function LoginPage(): ReactElement {
    const { store, snapshot } = useSession();
    const router = useRouter();
    const { method } = loginRoute.useSearch();
    if (snapshot.mode === 'accounts') {
      if (!snapshot.supabase) return <SupabaseWait failed={snapshot.supabaseFailed} />;
      return (
        <SignInRoute
          passwordSignIn={method === 'password'}
          // The callback route exchanges the Supabase session, as it does for an emailed link.
          onPasswordSignedIn={() => void router.navigate({ to: '/auth/callback', replace: true })}
          auth={snapshot.supabase}
          turnstileSiteKey={snapshot.config?.turnstileSiteKey ?? null}
          notice={snapshot.signInNotice}
        />
      );
    }
    // LoginRoute navigates to `/` itself; `/` then takes the return path.
    return <LoginRoute onSignedIn={() => store.markSignedIn()} />;
  }
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  beforeLoad: async ({ context }) => {
    const snapshot = await currentSession(context.session);
    if (snapshot.mode !== 'accounts') throw notFound();
  },
  component: function AuthCallbackPage(): ReactElement {
    const { store, snapshot } = useSession();
    const router = useRouter();
    if (!snapshot.supabase) return <SupabaseWait failed={snapshot.supabaseFailed} />;
    return (
      <AuthCallbackRoute
        auth={snapshot.supabase}
        onSignedIn={() => {
          store.markSignedIn();
          void router.navigate({ to: '/', replace: true });
        }}
        onFailed={(message) => {
          store.setSignInNotice(message);
          void router.navigate({ to: '/login', replace: true });
        }}
      />
    );
  }
});

const publishedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$linkId',
  beforeLoad: async ({ context }) => {
    await context.session.ready();
  },
  component: function PublishedPage(): ReactElement {
    const { linkId } = publishedRoute.useParams();
    const { snapshot } = useSession();
    return (
      <PublishedRoute
        linkId={linkId}
        signedIn={snapshot.status === 'signed-in'}
        activeWorkspaceId={snapshot.workspaceId}
      />
    );
  }
});

// Print and card run in a headless browser that has never signed in: no session request (contracts §7).
const printRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/print/$exportId',
  component: function PrintPage(): ReactElement {
    return <PrintRoute exportId={printRoute.useParams().exportId} />;
  }
});

const cardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/card/$exportId',
  component: function CardPage(): ReactElement {
    return <CardRoute exportId={cardRoute.useParams().exportId} />;
  }
});

/** The one way the app leaves for another origin. An object, so a test can vi.spyOn(leaveApp, 'assign');
 *  jsdom's window.location.assign is non-configurable and cannot be spied on. */
export const leaveApp = { assign: (url: string): void => window.location.assign(url) };

/** MCP OAuth pages exist only in accounts mode with the flag on; otherwise they are not found (charter I5). */
async function requireOAuth(session: SessionStore): Promise<SessionSnapshot> {
  const snapshot = await currentSession(session);
  if (snapshot.mode !== 'accounts' || snapshot.config?.mcpOauth !== true) throw notFound();
  return snapshot;
}

const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  validateSearch: (s: Record<string, unknown>): { authorization_id?: string } => ({
    authorization_id: typeof s.authorization_id === 'string' && s.authorization_id ? s.authorization_id : undefined
  }),
  beforeLoad: async ({ context, location }) => {
    const snapshot = await requireOAuth(context.session);
    // Cookie session gone: remember this URL (it keeps authorization_id) and sign in.
    if (snapshot.status !== 'signed-in') {
      rememberReturnPath(location.href);
      throw redirect({ to: '/login' });
    }
  },
  component: function ConsentPage(): ReactElement {
    const { snapshot } = useSession();
    const router = useRouter();
    const { authorization_id: authorizationId } = consentRoute.useSearch();
    if (!snapshot.supabase) return <SupabaseWait failed={snapshot.supabaseFailed} />;
    // Keyed: the screen computes its state once on mount, so a changed authorization remounts it.
    return (
      <ConsentRoute
        key={authorizationId ?? ''}
        auth={snapshot.supabase}
        authorizationId={authorizationId ?? null}
        onSignInNeeded={() => {
          rememberReturnPath(router.state.location.href);
          void router.navigate({ to: '/login' });
        }}
        redirect={(url) => leaveApp.assign(url)}
      />
    );
  }
});

/** Shell-less routes; session.ts's isPublicPath must agree (a routes test checks it). */
const accountDeletedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-deleted',
  beforeLoad: async ({ context }) => {
    await context.session.ready();
  },
  component: function AccountDeletedPage(): ReactElement {
    const { snapshot } = useSession();
    return <AccountDeletedRoute graceDays={snapshot.config?.deletionGraceDays ?? null} />;
  }
});

export const publicRoutes = [
  loginRoute,
  authCallbackRoute,
  consentRoute,
  accountDeletedRoute,
  publishedRoute,
  printRoute,
  cardRoute
];

// The account page is signed-in and shell-less, but not public: it stays out of publicRoutes.
const accountAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/agents',
  beforeLoad: async ({ context, location }) => {
    await requireOAuth(context.session);
    await requireSignedIn(context.session, location.href);
  },
  component: function AccountAgentsPage(): ReactElement {
    // The page handles a null client itself (Revoke shows the grant-step banner), so the list renders without Supabase.
    return <ConnectedAgentsRoute auth={useSession().snapshot.supabase} />;
  }
});

// --- the workspace ----------------------------------------------------------

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$workspaceId',
  component: WorkspaceLayout,
  errorComponent: () => <StatusState kind="error" title="This workspace could not be opened" page />,
  beforeLoad: async ({ context, params, location }) => {
    await requireSignedIn(context.session, location.href);
    try {
      await context.session.enterWorkspace(params.workspaceId);
    } catch (error) {
      if (error instanceof NotAMemberError) throw notFound();
      throw error;
    }
  }
});

const reportsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'reports',
  component: Outlet,
  staticData: { nav: { label: 'Reports', icon: 'reports', placement: 'main', order: 1 }, crumb: 'Reports' }
});

const reportsIndexRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/',
  validateSearch: (s: Record<string, unknown>): { create?: 'csv' | 'new' | 'sheet'; google_error?: GoogleErrorCode } => ({
    create: s.create === 'csv' || s.create === 'new' || s.create === 'sheet' ? s.create : undefined,
    google_error: isGoogleErrorCode(s.google_error) ? s.google_error : undefined
  }),
  component: function ReportsPage(): ReactElement {
    const { snapshot } = useSession();
    return <HomeRoute config={snapshot.config} />;
  }
});

const reportRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '$reportId',
  staticData: { rail: true },
  validateSearch: (
    s: Record<string, unknown>
  ): { mode?: 'preview'; panel?: 'history'; version?: number; share?: 1; google_error?: GoogleErrorCode } => ({
    google_error: isGoogleErrorCode(s.google_error) ? s.google_error : undefined,
    mode: s.mode === 'preview' ? ('preview' as const) : undefined,
    panel: s.panel === 'history' ? ('history' as const) : undefined,
    version: typeof s.version === 'number' ? s.version : undefined,
    share: s.share === 1 || s.share === '1' ? (1 as const) : undefined
  }),
  component: function ReportPage(): ReactElement {
    const { reportId } = reportRoute.useParams();
    const { snapshot } = useSession();
    return <ReportRoute reportId={reportId} config={snapshot.config} />;
  }
});

const publishedLinksRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'published',
  staticData: {
    nav: { label: 'Published links', icon: 'published', placement: 'main', order: 2 },
    crumb: 'Published links'
  },
  component: PublishedLinksRoute
});

const agentsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'agents',
  staticData: { nav: { label: 'Agents', icon: 'agents', placement: 'main', order: 3 }, crumb: 'Agents' },
  component: function AgentsPage(): ReactElement {
    const { snapshot } = useSession();
    return <ConnectRoute config={snapshot.config} />;
  }
});

const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings',
  validateSearch: (s: Record<string, unknown>): { google_error?: GoogleErrorCode } => ({
    google_error: isGoogleErrorCode(s.google_error) ? s.google_error : undefined
  }),
  component: SettingsLayout,
  staticData: { nav: { label: 'Settings', icon: 'settings', placement: 'bottom', order: 4 }, crumb: 'Settings' }
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/w/$workspaceId/settings/general', params, replace: true });
  }
});

const settingsSection = <Path extends string>(
  path: Path,
  label: string,
  order: number,
  component: () => ReactElement
) =>
  createRoute({
    getParentRoute: () => settingsRoute,
    path,
    staticData: { settingsSection: { label, order } },
    component
  });

const settingsSectionRoutes = [
  settingsSection('general', 'General', 1, GeneralSettings),
  settingsSection('brand', 'Brand', 2, BrandSettings),
  settingsSection('theme', 'Report theme', 3, ThemeSettings),
  settingsSection('tokens', 'API tokens', 4, TokenSettings),
  settingsSection('sources', 'Sources', 5, SourcesSettings),
  settingsSection('danger', 'Danger zone', 6, DangerZone)
];

// TanStack fuzzy-matches an unknown /settings/<x> to the settings layout as a
// success; this splat ranks below the explicit sections and makes it not-found.
// Its own notFoundComponent keeps the page inside the shell and marks this
// match notFound (without one, the root renders it and the match stays pending).
const settingsSplatRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '$',
  notFoundComponent: NotFoundPage,
  beforeLoad: () => {
    throw notFound();
  }
});

// --- redirects from the old URL shapes (charter I3) ---------------------------

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context, location }) => {
    // Read (and removed) first: this is how sign-in returns to the page it interrupted.
    const stored = takeReturnPath();
    if (stored !== '/') throw redirect({ href: stored, replace: true });
    await requireSignedIn(context.session);
    const workspaceId = await context.session.defaultWorkspace(location.searchStr);
    throw redirect({ to: '/w/$workspaceId/reports', params: { workspaceId }, replace: true });
  }
});

const oldReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/$reportId',
  beforeLoad: async ({ context, location, params }) => {
    await requireSignedIn(context.session, location.href);
    const workspaceId = await context.session.defaultWorkspace(location.searchStr);
    throw redirect({
      to: '/w/$workspaceId/reports/$reportId',
      params: { workspaceId, reportId: params.reportId },
      replace: true
    });
  }
});

const oldPage = (path: string, to: string) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: async ({ context, location }) => {
      await requireSignedIn(context.session, location.href);
      const workspaceId = await context.session.defaultWorkspace(location.searchStr);
      throw redirect({ to, params: { workspaceId }, replace: true });
    }
  });

const routeTree = rootRoute.addChildren([
  ...publicRoutes,
  accountAgentsRoute,
  workspaceRoute.addChildren([
    reportsRoute.addChildren([reportsIndexRoute, reportRoute]),
    publishedLinksRoute,
    agentsRoute,
    settingsRoute.addChildren([settingsIndexRoute, ...settingsSectionRoutes, settingsSplatRoute])
  ]),
  homeRoute,
  oldReportRoute,
  oldPage('/workspace', '/w/$workspaceId/settings/general'),
  oldPage('/connect', '/w/$workspaceId/agents')
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPendingComponent: () => <StatusState kind="loading" title="Loading…" page />
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// Types every `navigate`, `redirect` and `Link` against this tree.
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
